import { array as codecArray, oneOf as codecOneOf } from 'purify-ts/Codec';
import { makeResponses } from '../response_factory';
import { utils as coreUtils, paginated, requestParams } from '@codegrade/api-core';
import { CreateProxySubmissionData, ExtendedWork, FeedbackWithoutReplies, FeedbackWithReplies, GradeHistory, MirrorFileResult, PatchGraderSubmissionData, PatchRubricResultResponse, PatchSubmissionData, Proxy, PutRubricResultSubmissionData, RootFileTreesJSON, WorkRubricItem, } from '@codegrade/api-models';
import { parsers } from '@codegrade/utils';
const makeResult = coreUtils.makeResult;
// eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
const parseJsonResponse = /* #__PURE__ */ parsers.parseJsonResponse;
export function submission(config) {
    const createProxyResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(Proxy)),
    });
    const deleteResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(async () => null),
    });
    const getResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(
        /* #__PURE__ */ parseJsonResponse(
        /* #__PURE__ */ codecOneOf([ExtendedWork, MirrorFileResult]))),
    });
    const patchResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(ExtendedWork)),
    });
    const deleteGraderResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(async () => null),
    });
    const patchGraderResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(async () => null),
    });
    const getFeedbackResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(
        /* #__PURE__ */ parseJsonResponse(
        /* #__PURE__ */ parsers.taggedUnion('type', [
            ['feedback-without-replies', FeedbackWithoutReplies],
            ['feedback-with-replies', FeedbackWithReplies],
        ]))),
    });
    const getGradeHistoryResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(GradeHistory))),
    });
    const getRootFileTreesResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(RootFileTreesJSON)),
    });
    const getAllRubricResultsResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(WorkRubricItem))),
    });
    const putRubricResultResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(
        /* #__PURE__ */ parseJsonResponse(PatchRubricResultResponse)),
    });
    return Object.freeze({
        /**
         * Create a proxy to view the files of the given submission through.
         *
         * This allows you to view files of a submission without authentication for a limited time.
         *
         * @param arg The arguments to the function.
         * @param arg.submissionId The submission for which the proxy should be created.
         * @param arg.requestBody The body of the request.
         * @returns The created proxy.
         */
        createProxy: async ({ submissionId, requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/submissions/${submissionId}/proxy`, createProxyResponse.v, JSON.stringify(CreateProxySubmissionData.encode(requestBody)));
            return result;
        },
        /**
         * Delete a submission and all its files.
         *
         * @param arg The arguments to the function.
         * @param arg.submissionId The submission to delete.
         * @returns Nothing
         */
        delete: async ({ submissionId, }) => {
            const result = await requestParams(config, 'DELETE', `/api/v1/submissions/${submissionId}`, deleteResponse.v, undefined);
            return result;
        },
        /**
         * Get the given submission (also called work) by id.
         *
         * @param arg The arguments to the function.
         * @param arg.submissionId The id of the submission
         * @param arg.type If passed this cause you not to receive a submission object. What you will receive will depend on the value passed. If you pass `zip` you will receive a zip file with all the files of the submission. If you pass `feedback` you will receive a text file with a textual representation of all the feedback given on this submission.
         * @param arg.owner This query parameter is only used when `type=='zip'`. It will determine which revision is used to generate the zip file.
         * @returns The requested submission, or one of the other types as requested by the `type` query parameter.
         */
        get: async ({ submissionId, type = undefined, owner = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/submissions/${submissionId}`, getResponse.v, undefined, {
                type,
                owner,
            });
            return result;
        },
        /**
         * Update the given submission with new values.
         *
         * @param arg The arguments to the function.
         * @param arg.submissionId The id of the submission.
         * @param arg.requestBody The body of the request.
         * @returns The updated submission.
         */
        patch: async ({ submissionId, requestBody, }) => {
            const result = await requestParams(config, 'PATCH', `/api/v1/submissions/${submissionId}`, patchResponse.v, JSON.stringify(PatchSubmissionData.encode(requestBody)));
            return result;
        },
        /**
         * Change the assigned grader of the given submission.
         *
         * @param arg The arguments to the function.
         * @param arg.submissionId The id of the submission.
         * @returns Empty response and a 204 status.
         */
        deleteGrader: async ({ submissionId, }) => {
            const result = await requestParams(config, 'DELETE', `/api/v1/submissions/${submissionId}/grader`, deleteGraderResponse.v, undefined);
            return result;
        },
        /**
         * Change the assigned grader of the given submission.
         *
         * @param arg The arguments to the function.
         * @param arg.submissionId The id of the submission.
         * @param arg.requestBody The body of the request.
         * @returns Empty response and a 204 status.
         */
        patchGrader: async ({ submissionId, requestBody, }) => {
            const result = await requestParams(config, 'PATCH', `/api/v1/submissions/${submissionId}/grader`, patchGraderResponse.v, JSON.stringify(PatchGraderSubmissionData.encode(requestBody)));
            return result;
        },
        /**
         * Get all feedback for a submission
         *
         * @param arg The arguments to the function.
         * @param arg.submissionId The submission of which you want to get the feedback.
         * @param arg.withReplies Do you want to include replies in with your comments? Starting with version "O.1" the default value will change to `True`.
         * @returns The feedback of this submission.
         */
        getFeedback: async ({ submissionId, withReplies = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/submissions/${submissionId}/feedbacks/`, getFeedbackResponse.v, undefined, {
                with_replies: withReplies,
            });
            return result;
        },
        /**
         * Get the grade history for the given submission.
         *
         * @param arg The arguments to the function.
         * @param arg.submissionId The submission for which you want to get the grade history.
         * @param arg.pageSize The size of a single page, maximum is 50.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns All the `GradeHistory` objects, which describe the history of this grade.
         */
        getGradeHistory: async ({ submissionId, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/submissions/${submissionId}/grade_history/`, getGradeHistoryResponse.v, undefined, {
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
        /**
         * Get all the file trees of a submission.
         *
         * @param arg The arguments to the function.
         * @param arg.submissionId The id of the submission of which you want to get the file trees.
         * @returns The student and teacher file tree, from the base/root directory of the submission.
         */
        getRootFileTrees: async ({ submissionId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/submissions/${submissionId}/root_file_trees/`, getRootFileTreesResponse.v, undefined);
            return result;
        },
        /**
         * Get the rubric results of a submission.
         *
         * @param arg The arguments to the function.
         * @param arg.submissionId The id of the submission
         * @param arg.pageSize The size of a single page, maximum is 50.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns The rubric results that are requested.
         */
        getAllRubricResults: async ({ submissionId, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/submissions/${submissionId}/rubricitems/`, getAllRubricResultsResponse.v, undefined, {
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
        /**
         * Select the given rubric items for the given submission.
         *
         * @param arg The arguments to the function.
         * @param arg.submissionId The submission to unselect the item for.
         * @param arg.requestBody The body of the request.
         * @param arg.copyLockedItems Should we maintain the selected items in locked rubric rows.
         * @returns The work of which you updated the rubric items.
         */
        putRubricResult: async ({ submissionId, requestBody, copyLockedItems = undefined, }) => {
            const result = await requestParams(config, 'PUT', `/api/v1/submissions/${submissionId}/rubricitems/`, putRubricResultResponse.v, JSON.stringify(PutRubricResultSubmissionData.encode(requestBody)), {
                copy_locked_items: copyLockedItems,
            });
            return result;
        },
    });
}
